/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ClipboardList,
  FolderOpen,
  Globe,
  Layers,
  LayoutDashboard,
  LogOut,
  Settings,
  Shield,
  Tag,
  User,
  X,
} from 'lucide-react';
import React, { useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { useLiveSessionContext } from '../context/LiveSessionContext';
import toastService from '../utils/toastService';
import AccountSwitcher from './AccountSwitcher';
import LiveSessionsPanel from './LiveSessionsPanel';
import { getLiveSessionIndicator, isLiveSessionServerOffline } from './LiveSessionServerNotice';
import { isSettingsRoute } from './SettingsPanel';
import { getLoginPath } from '../constants/loginRoutes';
import { getBrandName, hasAnyRoleKey } from '../utils/scope';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const navItems = [
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/service-requests/types', icon: Tag, label: `${SERVICE_REQUEST_LABEL.singular} Types`, permission: 'service_requests.create', platformOnly: true },
  { to: '/service-requests', icon: ClipboardList, label: SERVICE_REQUEST_LABEL.plural, permission: 'service_requests.view', excludePrefixes: ['/service-requests/types'] },
  { to: '/media-library', icon: FolderOpen, label: 'Media Library', permission: 'service_requests.view' },
  { to: '/profile', icon: Settings, label: 'Settings', isSettings: true },
];

const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { user, accountName, logout, currentLogoUrl } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  const isPlatformAdmin = user?.scope === 'platform';
  const isAccountAdmin = user?.scope === 'account';
  const canSwitchContext = isPlatformAdmin || isAccountAdmin;

  const currentContextLabel = useMemo(() => {
    if (user?.subAccountUID) {
      return user?.subAccountName || SUB_ACCOUNT_LABEL.singular;
    }
    if (isPlatformAdmin) {
      return accountName || user?.accountName || `All ${ACCOUNT_LABEL.plural}`;
    }
    if (isAccountAdmin) {
      return `All ${SUB_ACCOUNT_LABEL.plural}`;
    }
    return accountName || user?.accountName || 'Default';
  }, [user?.subAccountUID, user?.subAccountName, user?.accountName, isPlatformAdmin, isAccountAdmin, accountName]);

  const ContextIcon = user?.subAccountUID ? Layers : (user?.accountUID ? Building2 : Globe);

  const userInitials = useMemo(() => {
    const first = user?.firstName?.[0] || '';
    const last = user?.lastName?.[0] || '';
    const combined = (first + last).toUpperCase();
    if (combined) return combined;
    if (user?.displayName) {
      const parts = user.displayName.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return user.displayName.slice(0, 2).toUpperCase();
    }
    return (user?.email?.[0] || 'U').toUpperCase();
  }, [user?.firstName, user?.lastName, user?.displayName, user?.email]);

  const formattedRole = useMemo(() => {
    if (user?.roleDisplayName) return user.roleDisplayName;
    if (!user?.role) return 'User';
    return user.role
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, [user?.role, user?.roleDisplayName]);

  const [liveSessionsPanelOpen, setLiveSessionsPanelOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [menuView, setMenuView] = useState('main'); // 'main' | 'switcher'
  const userMenuRef = useRef(null);

  const {
    sessions: liveSessions,
    joinedRoom: liveSessionJoinedRoom,
    join: joinLiveSession,
    leave: leaveLiveSession,
    authorized: liveSessionAuthorized,
    serverStatus: liveSessionServerStatus,
    reconnect: reconnectLiveSession,
    reconnectAttemptsLeft: liveSessionReconnectAttemptsLeft,
  } = useLiveSessionContext();
  const liveSessionOffline = isLiveSessionServerOffline(liveSessionServerStatus);
  const liveSessionIndicator = getLiveSessionIndicator(liveSessionServerStatus);
  const LiveSessionIcon = liveSessionIndicator.Icon;
  const showLiveSessionCount = !liveSessionOffline && liveSessions.length > 0;
  let liveSessionsLabel = 'Live Sessions';
  if (liveSessionOffline) {
    liveSessionsLabel = `Live Sessions (${liveSessionIndicator.label})`;
  } else if (liveSessions.length > 0) {
    liveSessionsLabel = `Live Sessions (${liveSessions.length})`;
  }

  // Automatically open user menu to switcher view if sub-account is required
  useEffect(() => {
    const handleSubAccountRequired = () => {
      setUserMenuOpen(true);
      setMenuView('switcher');
    };
    window.addEventListener('sub-account-required', handleSubAccountRequired);
    return () => window.removeEventListener('sub-account-required', handleSubAccountRequired);
  }, []);

  // Close user menu on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
        setMenuView('main');
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Reset menu view to main whenever user menu is closed
  useEffect(() => {
    if (!userMenuOpen) {
      setMenuView('main');
    }
  }, [userMenuOpen]);

  // Close user menu or mobile drawer on escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (userMenuOpen) {
          if (menuView === 'switcher') {
            setMenuView('main');
          } else {
            setUserMenuOpen(false);
          }
        } else if (isOpen && window.innerWidth < 1024) {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [userMenuOpen, menuView, isOpen, onClose]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isOpen && window.innerWidth < 1024) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close user menu and mobile drawer only on actual route change
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setUserMenuOpen(false);
      setMenuView('main');
      if (window.innerWidth < 1024 && onClose) {
        onClose();
      }
    }
  }, [location.pathname, onClose]);

  const handleToggleUserMenu = () => {
    setUserMenuOpen((prev) => {
      const next = !prev;
      setMenuView('main');
      return next;
    });
  };

  const handleJoinLiveSession = async (session) => {
    try {
      await joinLiveSession(session);
      setLiveSessionsPanelOpen(false);
    } catch {
      toastService.error('Failed to join the live session');
    }
  };

  const isSubAccountContext = !!user?.subAccountUID;
  const logoSrc = currentLogoUrl || logo;
  const logoAlt = currentLogoUrl ? `${isSubAccountContext ? SUB_ACCOUNT_LABEL.singular : ACCOUNT_LABEL.singular} Logo` : 'Send2Me Logo';
  const brandName = getBrandName(user, accountName);

  const handleProfileClick = () => {
    setUserMenuOpen(false);
    setMenuView('main');
    if (window.innerWidth < 1024) {
      onClose();
    }
    navigate('/profile');
  };

  const handleLogout = async () => {
    setUserMenuOpen(false);
    setMenuView('main');
    if (window.innerWidth < 1024) {
      onClose();
    }
    await logout();
    navigate(getLoginPath());
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-[250] lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar Content */}
      <aside
        aria-label="Main Navigation"
        className={`
          fixed inset-y-0 left-0 z-[300] bg-base-200 
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          w-72 max-w-[85vw] sm:w-80 lg:relative lg:inset-auto lg:h-full
          ${isCollapsed ? 'lg:w-20' : 'lg:w-72'}
          flex flex-col lg:rounded-xl border-r border-base-content/5 shadow-2xl lg:shadow-none
        `}
      >
        {/* Glow backdrop inside rounded container */}
        <div className='absolute inset-0 overflow-hidden lg:rounded-xl pointer-events-none'>
          <div className='absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary/20 blur-[120px] rounded-full pointer-events-none'></div>
          <div className='absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-primary/10 blur-[120px] rounded-full pointer-events-none'></div>
        </div>

        {/* Desktop Divider Collapse/Expand Toggle Arrow Button */}
        <div
          className="hidden lg:flex absolute -right-3.5 top-9 z-50 tooltip tooltip-right tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap"
          data-tip={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
        >
          <button
            onClick={onToggleCollapse}
            className="w-7 h-7 bg-base-100 border border-base-content/15 rounded-full shadow-md flex items-center justify-center text-base-content/60 hover:text-primary hover:bg-base-100 hover:border-primary/40 hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer group"
            aria-label={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
          >
            <ChevronLeft
              size={16}
              className={`transition-transform duration-300 text-base-content/70 group-hover:text-primary ${isCollapsed ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        <div className={`flex items-center justify-between px-6 py-6 pt-8 lg:py-8 lg:pt-8 ${isCollapsed ? 'lg:px-0 lg:justify-center' : 'lg:px-8'}`}>
          <div className={`flex items-center gap-3 min-w-0 ${isCollapsed ? 'lg:justify-center' : ''}`}>
            <div className="flex-shrink-0 bg-base-100 shadow-lg p-1 rounded-xl transition-all duration-300 border border-base-content/10 w-10 h-10">
              <img src={logoSrc} alt={logoAlt} className="w-full h-full object-contain" onError={(e) => { e.currentTarget.src = logo; e.currentTarget.onerror = null; }} />
            </div>
            <span
              className={`text-base-content font-black text-xl tracking-tight whitespace-nowrap truncate min-w-0 uppercase custom-line-break ${isCollapsed ? 'block lg:hidden' : 'block'}`}
              title={brandName}
            >
              {brandName}
            </span>
          </div>

          {/* Close button for mobile */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close navigation"
            className="lg:hidden p-2 text-base-content/60 hover:text-primary hover:bg-base-content/10 rounded-xl transition-colors cursor-pointer"
          >
            <X size={22} />
          </button>
        </div>

        <nav className={`flex-1 ${isCollapsed ? 'lg:overflow-visible' : 'lg:overflow-y-auto'} overflow-y-auto no-scrollbar py-2 space-y-2 px-4 ${isCollapsed ? 'lg:px-2' : 'lg:px-4'}`}>
          {navItems
            .filter(item => {
              if (item.platformOnly && user?.scope !== 'platform') { return false; }
              if (hasAnyRoleKey(user, item.excludeRoleKeys)) { return false; }
              if (!item.permission) { return true; }
              const permissionParts = item.permission.split('.');
              if (permissionParts.length === 2) {
                const [resource, action] = permissionParts;
                return can[resource] && can[resource][action] && can[resource][action]();
              }
              return true;
            })
            .map((item) => {
              const { to, icon: Icon, label, end, excludePrefixes, isSettings } = item;
              const isExcluded = excludePrefixes?.some((prefix) => location.pathname.startsWith(prefix));
              const isItemActive = isSettings
                ? isSettingsRoute(location.pathname)
                : (location.pathname === to || (!end && location.pathname.startsWith(`${to}/`))) && !isExcluded;

              return (
                <div
                  key={to}
                  className={`w-full ${isCollapsed ? 'lg:tooltip lg:tooltip-right lg:tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap' : ''}`}
                  data-tip={isCollapsed ? label : undefined}
                >
                  <NavLink
                    to={to}
                    end={end}
                    onClick={() => {
                      if (window.innerWidth < 1024) { onClose(); }
                    }}
                    aria-label={label}
                    className={
                      `relative flex items-center gap-3 py-3 rounded-xl text-sm font-medium transition-all duration-300 group px-4 ${isCollapsed ? 'lg:justify-center lg:px-0' : 'lg:px-4'
                      } ${isItemActive
                        ? 'bg-primary text-primary-content shadow-lg font-bold'
                        : 'text-base-content/60 hover:bg-base-content/10 hover:text-primary'
                      }`
                    }
                  >
                    <Icon size={22} className="transition-transform duration-300 group-hover:scale-110 flex-shrink-0" />
                    <span className={`whitespace-nowrap ${isCollapsed ? 'block lg:hidden' : 'block'}`}>{label}</span>
                  </NavLink>
                </div>
              );
            })}
        </nav>

        {/* Active Sessions — visible to anyone with service_requests.view, same as the broadcast icon on the Service Requests page */}
        {can.service_requests.view() && liveSessionAuthorized && (
        <div className={`px-4 py-0 mb-2 transition-all duration-300 ${isCollapsed ? 'lg:px-2' : ''}`}>
          <div
            className={`w-full ${isCollapsed ? 'lg:tooltip lg:tooltip-right lg:tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap' : ''}`}
            data-tip={isCollapsed ? liveSessionsLabel : undefined}
          >
            <button
              type="button"
              onClick={() => setLiveSessionsPanelOpen(true)}
              aria-label={liveSessionsLabel}
              className={`relative flex items-center gap-3 p-2 w-full bg-base-100/50 border border-base-content/10 rounded-2xl cursor-pointer hover:bg-base-content/10 transition-colors ${isCollapsed ? 'lg:justify-center' : ''}`}
            >
              <div className={`relative w-8 h-8 flex items-center justify-center rounded-xl shrink-0 ${liveSessionIndicator.boxClass}`}>
                <LiveSessionIcon size={18} className={liveSessionIndicator.iconClass} />
                {liveSessionOffline && (
                  <span className={`absolute -top-0.5 -right-0.5 inline-flex rounded-full h-2.5 w-2.5 ring-2 ring-base-100 ${liveSessionServerStatus === 'disconnected' ? 'bg-error' : 'bg-warning'}`} />
                )}
                {showLiveSessionCount && (
                  <span className={`absolute -top-1 -right-1 flex h-4 w-4 ${isCollapsed ? 'hidden lg:flex' : 'hidden'}`}>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-75" />
                    <span className="relative inline-flex items-center justify-center rounded-full h-4 w-4 bg-error text-[10px] leading-none text-white font-bold">
                      {liveSessions.length}
                    </span>
                  </span>
                )}
              </div>
              <div className={`flex-1 flex items-center justify-between min-w-0 ${isCollapsed ? 'flex lg:hidden' : 'flex'}`}>
                <span className="flex flex-col min-w-0 text-left">
                  <span className="text-base-content text-xs font-black truncate">
                    Live Sessions
                  </span>
                  {liveSessionOffline && (
                    <span className={`text-[10px] font-bold truncate ${liveSessionServerStatus === 'disconnected' ? 'text-error' : 'text-warning'}`}>
                      {liveSessionIndicator.label}
                    </span>
                  )}
                </span>
                {showLiveSessionCount && (
                  <span className="relative flex h-4 w-4 shrink-0">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-error opacity-75" />
                    <span className="relative inline-flex items-center justify-center rounded-full h-4 w-4 bg-error text-[10px] leading-none text-white font-bold">
                      {liveSessions.length}
                    </span>
                  </span>
                )}
              </div>
            </button>
          </div>
        </div>
        )}

        <div className={`p-4 mt-auto transition-all duration-300 ${isCollapsed ? 'lg:px-2' : 'lg:px-4'}`}>
          <div ref={userMenuRef} className="relative w-full">
            <div
              className={`w-full ${!userMenuOpen && isCollapsed ? 'lg:tooltip lg:tooltip-right lg:tooltip-primary before:text-xs before:font-semibold before:whitespace-nowrap' : ''}`}
              data-tip={!userMenuOpen && isCollapsed ? `${user?.displayName || 'User'} (${formattedRole})` : undefined}
            >
              <button
                type="button"
                onClick={handleToggleUserMenu}
                aria-haspopup="menu"
                aria-expanded={userMenuOpen}
                aria-label={user?.displayName || "User menu"}
                className={`w-full flex items-center gap-3 p-2.5 ${isCollapsed ? 'lg:justify-center lg:p-2' : 'lg:gap-3 lg:p-2.5'} rounded-2xl glass-panel !bg-base-100/30 hover:!bg-base-100/60 border border-base-content/5 hover:border-primary/30 transition-all duration-200 cursor-pointer group text-left ${userMenuOpen ? 'ring-2 ring-primary/40 !bg-base-100/70 shadow-lg' : ''}`}
              >
                <div className="relative shrink-0">
                  <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-secondary text-primary-content flex items-center justify-center font-black text-xs sm:text-sm flex-shrink-0 shadow-md border border-base-100 ring-2 ring-primary/20 tracking-wider transition-transform duration-200 group-hover:scale-105">
                    {userInitials}
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-base-200 rounded-full" />
                </div>
                <div className={`min-w-0 flex-1 ${isCollapsed ? 'block lg:hidden' : 'block'}`}>
                  <p className="text-base-content text-sm font-black truncate group-hover:text-primary transition-colors">
                    {user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User'}
                  </p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className="text-base-content/50 text-[10px] truncate uppercase tracking-widest font-black">
                      {formattedRole}
                    </span>
                    {(accountName || user?.subAccountName || user?.accountName) && (
                      <>
                        <span className="text-base-content/20 text-[10px]">•</span>
                        <span
                          className="text-base-content/40 text-[10px] truncate max-w-[85px] font-medium"
                          title={user?.subAccountName || accountName || user?.accountName}
                        >
                          {user?.subAccountName || accountName || user?.accountName}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <ChevronsUpDown
                  size={16}
                  className={`text-base-content/40 group-hover:text-primary transition-colors duration-200 flex-shrink-0 ${isCollapsed ? 'block lg:hidden' : 'block'} ${userMenuOpen ? 'text-primary' : ''}`}
                />
              </button>
            </div>

            {userMenuOpen && (
              <div
                role="menu"
                className={`
                  absolute z-50 bg-base-200/95 backdrop-blur-xl border border-base-content/10 rounded-2xl shadow-2xl overflow-hidden
                  bottom-full left-0 right-0 mb-2 w-full max-w-[calc(100vw-2rem)] max-h-[75vh] overflow-y-auto
                  ${isCollapsed
                    ? 'lg:bottom-0 lg:left-full lg:ml-3 lg:w-80 lg:max-w-[calc(100vw-6rem)] lg:mb-0 lg:right-auto'
                    : 'lg:bottom-full lg:left-0 lg:right-0 lg:mb-2 lg:w-full lg:ml-0'
                  }
                `}
              >
                {menuView === 'switcher' ? (
                  <AccountSwitcher
                    onBack={() => setMenuView('main')}
                    onComplete={() => {
                      setUserMenuOpen(false);
                      setMenuView('main');
                      if (window.innerWidth < 1024) {
                        onClose();
                      }
                    }}
                  />
                ) : (
                  <>
                    {/* User Header Summary inside Popover */}
                    <div className="p-3.5 bg-base-100/60 border-b border-base-content/10">
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary via-primary/90 to-secondary text-primary-content flex items-center justify-center font-black text-sm shadow-md border border-base-100 ring-2 ring-primary/20">
                            {userInitials}
                          </div>
                          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 border-2 border-base-100 rounded-full" title="Active" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-black text-base-content truncate leading-tight">
                            {user?.displayName || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'User'}
                          </p>
                          {user?.email && (
                            <p className="text-[11px] font-medium text-base-content/60 truncate mt-0.5" title={user.email}>
                              {user.email}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Badges for role and account context */}
                      <div className="mt-2.5 pt-2.5 border-t border-base-content/5 flex flex-wrap items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-primary/10 text-primary border border-primary/20">
                          <Shield size={10} className="shrink-0" />
                          {formattedRole}
                        </span>
                        {(accountName || user?.subAccountName || user?.accountName) && (
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold text-base-content/60 bg-base-content/5 border border-base-content/10 truncate max-w-[140px]"
                            title={user?.subAccountName || accountName || user?.accountName}
                          >
                            <ContextIcon size={10} className="shrink-0" />
                            <span className="truncate">{user?.subAccountName || accountName || user?.accountName}</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Sub-item Navigation Actions */}
                    <div className="p-1.5 space-y-1">
                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleProfileClick}
                        className={`w-full flex items-center gap-3 p-2 rounded-xl text-left transition-all duration-150 group cursor-pointer ${
                          location.pathname === '/profile'
                            ? 'bg-primary text-primary-content shadow-sm font-bold'
                            : 'hover:bg-base-content/10 text-base-content/80 hover:text-base-content'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${
                          location.pathname === '/profile'
                            ? 'bg-primary-content/20 text-primary-content'
                            : 'bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-content'
                        }`}>
                          <User size={15} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold leading-tight truncate">My Profile</p>
                          <p className={`text-[10px] truncate ${location.pathname === '/profile' ? 'text-primary-content/80' : 'text-base-content/50'}`}>
                            Account settings & personal info
                          </p>
                        </div>
                        <ChevronRight
                          size={14}
                          className={`shrink-0 transition-opacity ${location.pathname === '/profile' ? 'opacity-100 text-primary-content' : 'opacity-0 group-hover:opacity-100 text-base-content/40'}`}
                        />
                      </button>

                      {/* Switch Account / Switch Sub-account option (only for Platform/Account Admins) */}
                      {canSwitchContext && (
                        <button
                          type="button"
                          role="menuitem"
                          onClick={() => setMenuView('switcher')}
                          className="w-full flex items-center gap-3 p-2 rounded-xl text-left transition-all duration-150 hover:bg-base-content/10 text-base-content/80 hover:text-base-content group cursor-pointer"
                        >
                          <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0 group-hover:bg-primary group-hover:text-primary-content transition-colors">
                            <ContextIcon size={15} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-bold leading-tight truncate">
                              {isPlatformAdmin ? `Switch ${ACCOUNT_LABEL.singular}` : `Switch ${SUB_ACCOUNT_LABEL.singular}`}
                            </p>
                            <p className="text-[10px] text-base-content/50 truncate">
                              {currentContextLabel}
                            </p>
                          </div>
                          <ChevronRight
                            size={14}
                            className="shrink-0 text-base-content/40 group-hover:text-base-content group-hover:translate-x-0.5 transition-all"
                          />
                        </button>
                      )}

                      <div className="border-t border-base-content/10 my-1" />

                      <button
                        type="button"
                        role="menuitem"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 p-2 rounded-xl text-left transition-all duration-150 hover:bg-rose-500/10 text-rose-500 hover:text-rose-600 group cursor-pointer"
                      >
                        <div className="w-8 h-8 rounded-lg bg-rose-500/10 text-rose-500 flex items-center justify-center shrink-0 group-hover:bg-rose-500 group-hover:text-white transition-colors">
                          <LogOut size={15} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-bold leading-tight truncate">Logout</p>
                          <p className="text-[10px] text-rose-500/70 truncate">
                            Sign out of your session
                          </p>
                        </div>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </aside>

      <LiveSessionsPanel
        isOpen={liveSessionsPanelOpen}
        sessions={liveSessions}
        joinedRoom={liveSessionJoinedRoom}
        serverStatus={liveSessionServerStatus}
        onReconnect={reconnectLiveSession}
        reconnectAttemptsLeft={liveSessionReconnectAttemptsLeft}
        onJoin={handleJoinLiveSession}
        onLeave={leaveLiveSession}
        onClose={() => setLiveSessionsPanelOpen(false)}
      />
    </>
  );
};

export default Sidebar;

Sidebar.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func,
};
